// Activo Fijo → Resumen → Dashboard.
import { useQuery } from '@tanstack/react-query';
import { Boxes, Calculator, ExternalLink, Landmark, Loader2, TrendingDown, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { MESES, afRpc, money } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface DashData {
  resumen: { n_activos: number; costo_total: number; deprec_total: number; valor_libros: number; n_totalmente_dep: number };
  por_estado: { estado: string; n: number }[];
  por_categoria: { categoria: string; n: number; valor_libros: number }[];
  corrida_mes: { anio: number; mes: number; existe: boolean; estado: string | null };
  ultimas_altas: { placa: string; nombre: string; fecha_adquisicion: string | null; costo: number }[];
  ultimos_movimientos: { tipo: string; fecha: string; placa: string; activo: string; monto: number }[];
}

function Ir({ to, title, children }: { to: string; title: string; children: React.ReactNode }) {
  const openTab = useTabsStore((s) => s.openTab);
  return <button onClick={() => openTab({ id: to, title, path: to })} className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-medium">{children} <ExternalLink className="w-3.5 h-3.5" /></button>;
}
function Kpi({ label, valor, icon }: { label: string; valor: string; icon?: React.ReactNode }) {
  return <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center justify-between">{label} {icon}</p><p className="text-2xl font-bold mt-1">{valor}</p></CardContent></Card>;
}

export function DashboardAF() {
  const { data, isFetching } = useQuery<DashData>({ queryKey: ['af-dashboard'], queryFn: () => afRpc<DashData>('af_dashboard') });
  if (isFetching || !data) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const r = data.resumen;
  const cm = data.corrida_mes;

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-2"><Landmark className="w-6 h-6 text-primary" /><h1 className="text-2xl font-bold">Activo Fijo</h1></div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi label="Activos" valor={r.n_activos.toLocaleString(LOCALE)} icon={<Boxes className="w-4 h-4 text-primary" />} />
        <Kpi label="Costo total" valor={money(r.costo_total)} icon={<Wallet className="w-4 h-4 text-primary" />} />
        <Kpi label="Depreciación acumulada" valor={money(r.deprec_total)} icon={<TrendingDown className="w-4 h-4 text-primary" />} />
        <Kpi label="Valor neto en libros" valor={money(r.valor_libros)} />
        <Kpi label="Totalmente depreciados" valor={r.n_totalmente_dep.toLocaleString(LOCALE)} />
      </div>

      {/* Corrida del mes */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Calculator className="w-4 h-4 text-primary" />
            <span>Depreciación de <b>{MESES[cm.mes - 1]} {cm.anio}</b>:</span>
            {cm.existe
              ? <span className={`rounded-full px-2 py-0.5 text-xs ${cm.estado === 'contabilizada' || cm.estado === 'cerrada' ? 'bg-estable-soft text-estable' : 'bg-pendiente-soft text-pendiente'}`}>{cm.estado}</span>
              : <span className="rounded-full bg-pendiente-soft text-pendiente px-2 py-0.5 text-xs">pendiente</span>}
          </div>
          <Ir to="/activo-fijo/depreciacion" title="Corrida de depreciación">Ir a la corrida</Ir>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Por categoría */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Valor en libros por categoría</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3 text-right">Activos</th><th className="py-2 pr-3 text-right">Valor en libros</th></tr></thead>
              <tbody>
                {data.por_categoria.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin activos</td></tr>}
                {data.por_categoria.map((c) => <tr key={c.categoria} className="border-b last:border-0"><td className="py-2 pr-3">{c.categoria}</td><td className="py-2 pr-3 text-right">{c.n}</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.valor_libros)}</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Estado del parque + últimas altas */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Parque por estado</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {data.por_estado.length === 0 && <span className="text-sm text-muted-foreground">Sin activos</span>}
              {data.por_estado.map((s) => <span key={s.estado} className="px-2 py-1 rounded-md bg-muted text-sm">{s.estado}: <b>{s.n}</b></span>)}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Últimas altas</p>
              {data.ultimas_altas.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
                <ul className="text-sm space-y-1">
                  {data.ultimas_altas.map((a, i) => <li key={i} className="flex justify-between"><span className="truncate mr-2"><span className="font-mono">{a.placa}</span> {a.nombre}</span><span className="font-mono tabular-nums text-muted-foreground">{money(a.costo)}</span></li>)}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Últimos movimientos (bajas/ventas) */}
      {data.ultimos_movimientos.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Últimas bajas y ventas</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th><th className="py-2 pr-3 text-right">Monto</th></tr></thead>
              <tbody>
                {data.ultimos_movimientos.map((m, i) => <tr key={i} className="border-b last:border-0"><td className="py-2 pr-3">{m.tipo}</td><td className="py-2 pr-3 whitespace-nowrap">{m.fecha}</td><td className="py-2 pr-3 font-mono">{m.placa}</td><td className="py-2 pr-3">{m.activo}</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(m.monto)}</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
